import { Box, Grid, TextField } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { saveDoctor } from '../../../../api/transports';
import { fetchDoctorTypes } from '../../../../redux/slices/transports';
import { useDispatch, useSelector } from '../../../../redux/store';
import { AwaitButton } from '../../../../reusable-components/await-button';
import PlacesAutocomplete from '../../../../reusable-components/google-maps/PlacesAutocomplete';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import DoctorTypeDropdown from '../Shared/DoctorTypeDropdown';

InlineLocationForm.propTypes = {
  disabled: PropTypes.bool,
  facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onAdded: PropTypes.func,
};

export default function InlineLocationForm({ disabled = false, facilityId, onAdded }) {
  const { enqueueSnackbar } = useSnackbar();
  const [hasSelectedAddress, setHasSelectedAddress] = useState(false);
  const [value, setValue] = useState({
    id: null,
    facilityId: facilityId ?? null,
    doctorName: null,
    doctorTypeId: null,
    buildingName: null,
    address: null,
    addressDisplay: null,
    city: null,
    state: null,
    deleteFlag: false,
  });
  const transportsState = useSelector((state) => state.transports);
  const dataIsLoading = transportsState?.isLoading ?? false;
  const doctorTypes = transportsState?.data?.doctorTypes ?? [];
  const dispatch = useDispatch();

  useEffect(() => {
    if (doctorTypes.length === 0) {
      dispatch(fetchDoctorTypes());
    }
  }, [dispatch, doctorTypes.length]);

  useEffect(() => {
    setValue((currentValue) => ({ ...currentValue, facilityId: facilityId ?? null }));
  }, [facilityId]);

  const handleSave = async () => {
    try {
      const response = await saveDoctor(value);
      const result = response.data;
      if (result.success) {
        setValue({
          id: null,
          facilityId: facilityId ?? null,
          doctorName: null,
          doctorTypeId: null,
          buildingName: null,
          address: null,
          addressDisplay: null,
          city: null,
          state: null,
          deleteFlag: false,
        });
        setHasSelectedAddress(false);
        enqueueSnackbar(result.message || 'Location updated successfully', { variant: 'success' });
        if (onAdded) onAdded(result.data);
      } else {
        enqueueSnackbar(result.message || 'Failed to update Location', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to update Location', { variant: 'error' });
    }
  };

  const renderTextField = (fieldKey, label) => (
    <TextField
      fullWidth
      size="small"
      value={value[fieldKey] || ''}
      onChange={(event) => setValue({ ...value, [fieldKey]: event.target.value })}
      label={label}
      sx={{ mt: 1 }}
      autoComplete="off"
    />
  );

  return (
    <Grid container spacing={2} sx={{ mt: 1, maxWidth: 1100 }}>
      <Grid item xs={12} sm={4}>
        {renderTextField('doctorName', 'Doctor Name')}
      </Grid>
      <Grid item xs={12} sm={4}>
        {renderTextField('buildingName', 'Building Name')}
      </Grid>
      <Grid item xs={12} sm={4}>
        <Box sx={{ mt: 1 }}>
          <DoctorTypeDropdown
            doctorTypes={doctorTypes}
            value={value.doctorTypeId}
            onChange={(doctorTypeId) => setValue({ ...value, doctorTypeId })}
            isLoading={dataIsLoading}
          />
        </Box>
      </Grid>
      <Grid item xs={12} sm={8}>
        <Box sx={{ mt: 1 }}>
          <PlacesAutocomplete
            value={value.address || ''}
            displayValue={value.addressDisplay ?? value.address ?? ''}
            onChange={(address) => {
              setHasSelectedAddress(Boolean(address));
              if (!address) {
                setValue((currentValue) => ({ ...currentValue, city: null, state: null }));
                return;
              }

              setValue((currentValue) => ({
                ...currentValue,
                address: address.address,
                addressDisplay: address.displayValue,
                city: address.city,
                state: address.state,
              }));
            }}
            label="Enter a location"
          />
        </Box>
      </Grid>
      <Grid item xs={12} sm={4}>
        <AwaitButton
          fullWidth
          variant="outlined"
          color="secondary"
          size="small"
          onClick={handleSave}
          disabled={disabled || !value.doctorTypeId || !value.facilityId || !hasSelectedAddress}
          sx={{ mt: 1, minHeight: 40, fontSize: '14px' }}
        >
          Save New Location
        </AwaitButton>
      </Grid>
    </Grid>
  );
}
